import React, { FormEvent, useState } from "react"

import { Identity, User, OMReference } from "@apptivity-lab/firmament-node-sdk"
import { AffAttendeeProfile } from "@firmament-packages/aff"

import TextInput, { emailValidator, isEmail } from "@components/forms/TextInput"
import AsyncButton from "@components/AsyncButton"
import { AffAttendeeProfileFieldset } from "@components/domain/AffAttendeeProfile"
import { useUpdateableState } from "@lib"

type Props = {
    className?: string,
    disabled?: boolean,
    error?: Error,
    identity?: Identity,
    attendeeProfile?: AffAttendeeProfile,
    onSubmit?: (identity: Identity, attendeeProfile: AffAttendeeProfile) => void,
}

const RegisterForm: React.FunctionComponent<Props> = ({
    className,
    disabled,
    error,
    identity,
    attendeeProfile,
    onSubmit
}) => {
    const [identifier, setIdentifier] = useUpdateableState(identity?.identifier)
    const [phoneNumber, setPhoneNumber] = useUpdateableState(attendeeProfile?.phoneNumber)
    const [identifierError, setIdentifierError] = useState<Error>()

    const [nameError, setNameError] = useState<Error>()
    const [profile, setProfile] = useUpdateableState<AffAttendeeProfile>(attendeeProfile)

    const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        let isValid = true

        if (!identifier) {
            setIdentifierError(new Error("Email address is required"))
            isValid = false
        } else if (!isEmail(identifier)) {
            setIdentifierError(new Error("Please enter a valid email address (example: someone@example.com)"))
            isValid = false
        }
        if (!profile || (profile?.name || "").trim().length === 0) {
            setNameError(new Error("First name is required"))
            isValid = false
        }

        if (!isValid) {
            return
        }

        const identity = new Identity({
            identityType: "email",
            provider: "apptivitylab.com",
            identifier: identifier!,
            user: profile?.user || new OMReference(User, User.createPlaceholder())
        })
        if (profile) {
            profile.phoneNumber = phoneNumber
        }
        onSubmit && onSubmit(identity, profile!)
    }

    return (
        <form id="registration-form" className={["registration-form", className || ""].join(" ")} onSubmit={onFormSubmit}>
            <AffAttendeeProfileFieldset
                attendeeProfile={profile}
                errors={nameError && { "name": nameError }}
                onChange={(model) => setProfile(model)} />

            <TextInput type="email" title="Email Address" isRequired={true}
                className="mt-[32px]"
                error={identifierError}
                validator={emailValidator}
                value={identifier}
                onChange={(text) => setIdentifier(text)} />

            <TextInput type="text" title="Phone Number" isRequired={false}
                className="mt-[32px]"
                value={phoneNumber || undefined}
                helperText={"Please enter your phone number so we can provide better support for you."}
                onChange={(text) => setPhoneNumber(text)} />

            <div className="flex mt-[32px] text-callout text-black-900 text-left">
                <input type="checkbox" className="input-checkbox my-auto" id="terms" name="terms" value="agree" required />
                <label htmlFor="terms">
                    I agree with the Alive Fit &amp; Free<br />
                    <a className="text-accent-900 underline" href="https://alivefitandfree.com/terms-conditions/" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a> and <a className="text-accent-900 underline" href="https://alivefitandfree.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </label>
            </div>

            {error && <p className="p-[8px] mt-[32px] text-alert-900 bg-alert-100">{error.message}</p>}

            <AsyncButton type="submit"
                className="mt-[40px] w-full button-primary"
                loading={disabled}>
                Register
            </AsyncButton>
        </form>
    )
}

export default RegisterForm
